import React, { useEffect, useMemo, useState } from 'react';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import parts from '../moduleCard/moduleParts.module.css';
import styles from './scales.module.css';
import ModuleCard from '../moduleCard/ModuleCard';
import ScaleInput from './ScaleInput';
import useScaleAutosave from './useScaleAutosave';
import { DataProviderInstance } from '../../../../api/DataProvider';
import { findOwnRecord, findPreviousRecord, formatDate, scaleRecords, summarizeValue } from '../../modules/scales/scaleValues';

const SAVE_LABELS = {
  pending: 'Alterações pendentes',
  saving: 'Salvando...',
  error: 'Não foi possível salvar. A próxima alteração tenta de novo.',
};

const SaveState = ({ state, savedAt }) => {
  if (state === 'saved' && savedAt) {
    return <span className={parts.muted}>Salvo às {savedAt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>;
  }
  if (state === 'error') return <span className={styles.saveError}>{SAVE_LABELS.error}</span>;
  return SAVE_LABELS[state] ? <span className={parts.muted}>{SAVE_LABELS[state]}</span> : null;
};

/**
 * Card de uma escala clínica na tela de atendimento. Tudo vem do catálogo
 * (`scale`): título, ícone, tipo de entrada. Cada clique grava; ao lado do
 * título fica o resultado da consulta anterior.
 *
 * `data`: avaliações do paciente (todas as escalas, `scaleAssessments`).
 */
const ScaleModule = ({ scale, data, patientId, appointmentId, appointmentDate, gender, collapsible = false }) => {
  const { SetPatientScaleAssessment } = DataProviderInstance();
  const records = useMemo(() => scaleRecords(data, scale.key), [data, scale.key]);
  const ownRecord = findOwnRecord(records, appointmentId);
  const previous = findPreviousRecord(records, appointmentId, appointmentDate);

  const [value, setValue] = useState(ownRecord ? ownRecord.value : null);
  const autosave = useScaleAutosave((next, done) => {
    SetPatientScaleAssessment(patientId, appointmentId, scale.key, next, (response) => done(response && response.status === 'SUCCESS'));
  });

  // Resincroniza com o servidor só quando não há alteração a caminho
  useEffect(() => {
    if (autosave.isDirty()) return;
    setValue(ownRecord ? ownRecord.value : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownRecord]);

  function handleChange(next) {
    setValue(next);
    autosave.queue(next);
  }

  const Icon = scale.icon || TuneOutlinedIcon;
  const previousText = previous ? `Na última consulta: ${summarizeValue(scale, previous.value)}, ${formatDate(previous.assessedAt)}` : null;

  return (
    <ModuleCard
      accent={scale.accent || 'teal'}
      title={scale.name}
      icon={<Icon />}
      description={scale.description}
      collapsible={collapsible}
      actions={previousText && <span className={parts.chip}>{previousText}</span>}
    >
      <div className={styles.content}>
        <ScaleInput scale={scale} value={value} onChange={handleChange} gender={gender} />
        <div className={styles.statusRow}>
          <span className={parts.muted}>{value ? 'Clique de novo para desmarcar' : 'Um clique marca o resultado'}</span>
          <SaveState state={autosave.saveState} savedAt={autosave.savedAt} />
        </div>
      </div>
    </ModuleCard>
  );
};

export default ScaleModule;
